import React from 'react';
import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import type {
  CreativeContributionWaterfall,
  CreativeContributionWaterfallComponentValue,
  CreativeDecisionProjection,
  MoneyAmountBinding,
  ValueBasisBinding,
} from '../../creativeDecisions';
import { CREATIVE_DECISIONS_TEST_IDS } from '../../testIds';
import { formatMoneyBinding } from './creativeDecisionFormat';

const COMPONENTS = [
  ['gross', 'Gross collected', 'total'],
  ['refunds', 'Refunds', 'deduction'],
  ['disputes', 'Disputes', 'signed_effect'],
  ['credits', 'Credits', 'signed_effect'],
  ['discounts', 'Discounts (pre-collection)', 'context'],
  ['fees', 'Fees', 'deduction'],
  ['fxCost', 'FX costs', 'deduction'],
  ['tax', 'Tax', 'deduction'],
  ['serviceCogs', 'Service COGS', 'deduction'],
  ['contributionBeforeAdSpend', 'Contribution before ad spend', 'subtotal'],
  ['mediaSpend', 'Media spend', 'deduction'],
  ['contributionAfterAdSpend', 'Contribution after ad spend', 'result'],
] as const;

type ComponentKey = typeof COMPONENTS[number][0];
type ComponentTone = typeof COMPONENTS[number][2];

export function CreativeContributionWaterfall({
  decision,
}: {
  decision: CreativeDecisionProjection;
}): React.ReactElement {
  const waterfall = decision.money.contributionWaterfall ?? buildCoverageOnlyWaterfall(decision);
  const rows = COMPONENTS.map(([key, label, tone]) => ({
    key,
    label,
    tone,
    value: waterfall.components[key],
  }));
  const comparable = comparableAmounts(rows.map((row) => row.value.amount));
  const maximum = Math.max(1, ...comparable.map((item) => Math.abs(item.amountMinor)));

  return (
    <Box
      data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerWaterfall}
      sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider' }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 750 }}>Contribution waterfall</Typography>
          <Typography variant="caption" color="text.secondary">
            Fixed order from collected value through customer-value adjustments, deductions, and media spend.
          </Typography>
        </Box>
        <Chip
          size="small"
          variant="outlined"
          color={waterfall.state === 'available' ? 'success' : waterfall.state === 'partial' ? 'warning' : 'default'}
          label={waterfall.state}
          data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerWaterfallState}
        />
      </Stack>

      {decision.money.contributionWaterfall ? null : (
        <Alert severity={waterfall.state === 'unavailable' ? 'info' : 'warning'} sx={{ mt: 1 }}>
          Exact deduction amounts are not projected for this deployment. Known totals are shown; missing components stay explicitly unavailable and are never derived from gross/net deltas.
        </Alert>
      )}

      <Stack component="ol" role="list" spacing={0.75} sx={{ m: 0, mt: 1.25, p: 0, listStyle: 'none' }}>
        {rows.map((row) => {
          const comparableAmount = comparable.find((item) => item.binding === row.value.amount)?.amountMinor;
          const amountLabel = signedAmountLabel(row.value.amount, row.tone);
          const stateLabel = row.value.state.replaceAll('_', ' ');
          return (
            <Box
              component="li"
              role="listitem"
              key={row.key}
              aria-label={`${row.label}: ${amountLabel}; ${stateLabel}; ${row.value.precision}`}
              data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerWaterfallStep(row.key)}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(150px, 0.8fr) minmax(130px, 1.2fr) auto' },
                gap: { xs: 0.5, sm: 1 },
                alignItems: 'center',
                p: 1,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: row.tone === 'result' ? 750 : 600 }}>
                  {row.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {`${stateLabel} · ${row.value.precision}`}
                </Typography>
              </Box>
              <Box
                aria-hidden="true"
                sx={{ height: 12, borderRadius: 999, bgcolor: 'action.hover', overflow: 'hidden' }}
              >
                {comparableAmount !== undefined && (
                  <Box
                    sx={{
                      width: `${Math.max(4, Math.abs(comparableAmount) / maximum * 100)}%`,
                      height: '100%',
                      borderRadius: 999,
                      bgcolor: waterfallColor(row.tone),
                    }}
                  />
                )}
              </Box>
              <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', textAlign: { sm: 'right' } }}>
                {amountLabel}
              </Typography>
            </Box>
          );
        })}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {comparable.length > 1
          ? 'Bar lengths compare only amounts sharing the same displayed currency basis.'
          : 'Bar comparison is unavailable until at least two components share one currency basis.'}
      </Typography>
    </Box>
  );
}

export function buildCoverageOnlyWaterfall(
  decision: CreativeDecisionProjection,
): CreativeContributionWaterfall {
  const { money } = decision;
  const coverageFor = (
    name: ValueBasisBinding['componentCoverage'][number]['component'],
  ): ValueBasisBinding['componentCoverage'][number] | undefined => {
    const bases = name === 'gross'
      ? [money.grossBasis, money.netCollectedBasis, money.contributionBasis]
      : name === 'refunds' || name === 'disputes'
        ? [money.netCollectedBasis, money.contributionBasis, money.grossBasis]
        : [money.contributionBasis, money.netCollectedBasis, money.grossBasis];
    for (const basis of bases) {
      const item = basis?.componentCoverage.find((candidate) => candidate.component === name);
      if (item) return item;
    }
    return undefined;
  };
  const component = (
    name: ValueBasisBinding['componentCoverage'][number]['component'],
    amount?: MoneyAmountBinding,
  ): CreativeContributionWaterfallComponentValue => {
    const coverageItem = coverageFor(name);
    if (amount) {
      return {
        amount,
        state: coverageItem?.state ?? 'complete',
        precision: amount.precision,
        reasonCodes: coverageItem ? [] : ['component_coverage_not_projected'],
      };
    }
    return {
      state: coverageItem?.state ?? 'unavailable',
      precision: coverageItem?.precision ?? 'unknown',
      reasonCodes: ['component_amount_not_projected'],
    };
  };
  const amount = (value?: MoneyAmountBinding): CreativeContributionWaterfallComponentValue => (
    value
      ? { amount: value, state: 'complete', precision: value.precision, reasonCodes: [] }
      : { state: 'unavailable', precision: 'unknown', reasonCodes: ['component_amount_not_projected'] }
  );
  const components: CreativeContributionWaterfall['components'] = {
    gross: component('gross', money.grossCollected),
    refunds: component('refunds'),
    disputes: component('disputes'),
    credits: component('credits'),
    discounts: component('discounts'),
    tax: component('tax'),
    fees: component('fees'),
    fxCost: component('fx_cost'),
    serviceCogs: component('service_cogs'),
    contributionBeforeAdSpend: amount(money.realizedContributionBeforeAdSpend),
    mediaSpend: amount(money.selectedWindowSpend),
    contributionAfterAdSpend: amount(money.realizedContributionAfterAdSpend),
  };
  const known = Object.values(components).filter((item) => item.amount).length;
  return {
    state: known === 0 ? 'unavailable' : 'partial',
    components,
    asOf: decision.freshness.asOf,
    sourceRefs: decision.freshness.sourceRevisionRefs,
    reasonCodes: ['exact_component_ledger_not_projected'],
  };
}

function comparableAmounts(bindings: readonly (MoneyAmountBinding | undefined)[]): Array<{
  binding: MoneyAmountBinding;
  amountMinor: number;
}> {
  const present = bindings.filter((value): value is MoneyAmountBinding => Boolean(value));
  const reference = present.find((value) => (
    value.reportingAmountMinor !== undefined && value.reportingCurrency && value.reportingCurrencyExponent !== undefined
  ));
  if (reference?.reportingCurrency) {
    return present.flatMap((binding) => (
      binding.reportingCurrency === reference.reportingCurrency
      && binding.reportingCurrencyExponent === reference.reportingCurrencyExponent
      && binding.reportingAmountMinor !== undefined
        ? [{ binding, amountMinor: binding.reportingAmountMinor }]
        : []
    ));
  }
  const native = present[0];
  if (!native) return [];
  return present.flatMap((binding) => (
    binding.originalCurrency === native.originalCurrency
    && binding.currencyExponent === native.currencyExponent
      ? [{ binding, amountMinor: binding.amountMinor }]
      : []
  ));
}

function signedAmountLabel(binding: MoneyAmountBinding | undefined, tone: ComponentTone): string {
  if (!binding) return 'n/a';
  const formatted = formatMoneyBinding(binding, true);
  return tone === 'deduction' && binding.amountMinor > 0 ? `−${formatted}` : formatted;
}

function waterfallColor(tone: ComponentTone): string {
  if (tone === 'deduction' || tone === 'signed_effect') return 'warning.main';
  if (tone === 'context') return 'text.secondary';
  if (tone === 'result') return 'success.main';
  if (tone === 'subtotal') return 'info.main';
  return 'primary.main';
}
